import { default as _Vue } from 'vue';
import { Store } from 'vuex';
import Amplify from '@aws-amplify/core';
import Auth from '@aws-amplify/auth';
import { CognitoUser, ISignUpResult } from 'amazon-cognito-identity-js';
import { AbstractService } from '@/plugins/podolib/lib/AbstractService';
import { get } from 'lodash';
import { navigatorLang } from '@/plugins/i18n';

// Service options type
export type TOptions = {
  // Amazon Cognito Identity Pool ID
  identityPoolId: string;
  // Amazon Cognito Region
  region: string;
  // Amazon Cognito User Pool ID
  userPoolId: string;
  // Amazon Cognito Web Client ID (26-char alphanumeric string)
  userPoolWebClientId: string;
};

// Store state type
export type TState = {
  [index: string]: any;
  user: any;
  _cc: string | null;
};

// Credentials type
export type TCredentials = {
  username: string;
  password: string;
};

// Confirmation type
export type TConfirmation = {
  username: TCredentials['username'];
  code: string;
};

/**
 * @author Damien CORLER <d.corler@zhortech.com>
 * @since 1.0.0
 * @version 1.0.0
 */
export default class CognitoService extends AbstractService<TOptions, TState> {
  /**
   * @param options
   */
  constructor(options: TOptions) {
    super(options, 'cognito');
  }

  /**
   * Initialize plugin
   */
  async init(Vue: typeof _Vue, store: Store<any>) {
    await super.init(Vue, store);

    // Amplify.Logger.LOG_LEVEL = "DEBUG";
    // Configure Amplify
    Amplify.configure({
      Auth: {
        // Manually set the authentication flow type. Default is 'USER_SRP_AUTH'
        authenticationFlowType: 'USER_PASSWORD_AUTH',
        // Defined options in build process
        ...this.options,
      },
    });

    this.$register(this.defaultState, {
      mutations: {
        SET_USER(state, value) {
          Vue.set(state, 'user', value);
        },
        SET__CC(state, value) {
          Vue.set(state, '_cc', value);
        },
      },
    });

    this.store.watch(
      (state) => state.pdc.cognito.user,
      (newValue: TState['user'], oldValue: TState['user']) => {
        const _oldSub = get(oldValue, 'attributes.sub');
        const _newSub = get(newValue, 'attributes.sub');
        if (_oldSub !== _newSub) {
          if (!_newSub) {
            this.Vue.prototype.$pdc.analytics.actions.reset();
          } else {
            this.Vue.prototype.$pdc.analytics.actions.identify(_newSub, {
              email: get(newValue, 'attributes.email'),
              email_verified: get(newValue, 'attributes.email_verified'),
            });
          }
        }
      },
    );

    this.Vue.prototype.$pdc.bus.on('app:reset', this.$clean.bind(this));
  }

  /**
   * Get default state of this store module
   */
  public get defaultState(): TState {
    return {
      ...super.defaultState,
      user: null,
      _cc: null,
    };
  }

  // ---------------------------------------------------------------------------
  // SHORTCUTS

  get user(): any {
    return this.$state('user');
  }

  get accessToken(): string | null {
    return this.user?.signInUserSession.accessToken.jwtToken;
  }

  /**
   * Get user
   *
   * @since 1.0.0
   */
  public async getUser(): Promise<CognitoUser> {
    try {
      let user: any;

      // Try to get user ...
      try {
        user = await Auth.currentAuthenticatedUser();
      } catch (e) {
        const _user = this.user;

        if (_user?.challengeName) {
          this.logger.warn('Challenge detected');
          return _user;
        }

        throw e;
      }

      // Store user
      this.$commit('SET_USER', user);

      // Configure Sentry
      this.Vue.prototype.$sentry.configureScope((scope: any) => {
        scope.setUser({
          id: user.attributes.sub,
          email: user.username,
        });
      });

      // Try to get session
      const session = await Auth.currentSession();

      const refreshed = session?.getAccessToken().getJwtToken() !== this.accessToken;

      /**
       * Check if token is expired
       */
      if (!session.isValid()) {
        // noinspection ExceptionCaughtLocallyJS
        throw new Error('Invalid token');
      }

      const _session = this.Vue.prototype.$pdc.repository.socket.session;

      if (_session && refreshed) {
        await this.Vue.prototype.$pdc.repository.socket.authenticate();
      }

      // Return user
      return user;
    } catch (e) {
      // Reset stored user
      this.$commit('SET_USER', null);
      // Bus
      this.Vue.prototype.$pdc.bus.emitDebug('auth:failed-get-user');

      /*if (e.message === 'No current user') {
        // Sentry
        this.Vue.prototype.$sentry.captureMessage(e.message, Severity.Log);
      } else {
        // Sentry
        this.Vue.prototype.$sentry.captureException(e);
      }*/

      // Throw error
      throw e;
    }
  }

  /**
   * Sign in
   * @param credentials
   *
   * @since 1.0.0
   */
  public async signIn(credentials: TCredentials): Promise<CognitoUser> {
    // Set CC
    this.$commit('SET__CC', credentials);
    // Logs
    this.logger.log('Sign in ...');
    // Try to sign in ...
    const user = await Auth.signIn(credentials);
    // Store user
    this.$commit('SET_USER', user);
    // Logs
    this.logger.success('Sign in completed');

    if (user.challengeName) {
      this.logger.log(`Challenge "${user.challengeName}" ...`);
    } else {
      // Bus
      this.Vue.prototype.$pdc.bus.emitDebug('app:init');
      // Open repository
      await this.Vue.prototype.$pdc.repository.socket.connect();
    }
    // Return user
    return user;
  }

  /**
   * Confirm sign in (MFA)
   *
   * @since 2.2.0
   */
  public async confirmSignIn(mfa: string): Promise<void> {
    // Logs
    this.logger.log('Confirm sign in ...');
    // Try to confirm sign in ...
    await Auth.confirmSignIn(this.user, mfa, 'SOFTWARE_TOKEN_MFA');
    // Refresh user
    await this.getUser();
    // Logs
    this.logger.success('Sign in confirmation completed');
    // Bus
    this.Vue.prototype.$pdc.bus.emitDebug('app:init');
    // Open repository
    await this.Vue.prototype.$pdc.repository.socket.connect();
  }

  /**
   * Sign up
   * @param credentials
   */
  public async signUp(credentials: TCredentials): Promise<ISignUpResult> {
    // Set CC
    this.$commit('SET__CC', credentials);
    // Logs
    this.logger.log('Sign up ...');

    // Return promise
    const response = await Auth.signUp({
      username: credentials.username,
      password: credentials.password,
      attributes: {
        locale: navigatorLang(),
      },
    });
    // Analytics
    this.Vue.prototype.$pdc.analytics.actions.identify(response.userSub, {
      email: (response.user as any).username,
      email_verified: response.userConfirmed,
    });
    return response;
  }

  /**
   * Confirm sign ups
   * @param confirmation
   */
  public confirmSignUp(confirmation: TConfirmation): Promise<void> {
    // Return promise
    return Auth.confirmSignUp(confirmation.username, confirmation.code);
  }

  /**
   * Resend sign up confirmation code
   * @param username
   */
  public resendSignUp(username: TCredentials['username']): Promise<string> {
    // Return promise
    return Auth.resendSignUp(username);
  }

  /**
   * Forgot password
   * @param username
   *
   * @since 1.0.0
   */
  public async forgotPassword(username: TCredentials['username']): Promise<CognitoUser> {
    // Logs
    this.logger.log('Forgot password ...');
    // Try to sign in ...
    const result = await Auth.forgotPassword(username);
    // Logs
    this.logger.success('Forgot password completed');
    // Return user
    return result;
  }

  /**
   * Forgot password submit
   * @param username
   * @param code
   * @param password
   *
   * @since 1.0.0
   */
  public async forgotPasswordSubmit(username: TCredentials['username'], code: string, password: string): Promise<void> {
    // Logs
    this.logger.log('Forgot password submit ...');
    // Try to sign in ...
    const result = await Auth.forgotPasswordSubmit(username, code, password);
    // Logs
    this.logger.success('Forgot password submit completed');
    // Return user
    return result;
  }

  /**
   * "NEW_PASSWORD_REQUIRED" challenge
   * @param password
   */
  public async completeNewPassword(password: TCredentials['password']): Promise<CognitoUser> {
    // Logs
    this.logger.log('Try to complete "NEW_PASSWORD_REQUIRED" challenge ...');
    // Challenge ...
    const user = await Auth.completeNewPassword(this.user, password, null);
    // Logs
    this.logger.success('Challenge "NEW_PASSWORD_REQUIRED" completed');
    // Open repository
    await this.Vue.prototype.$pdc.repository.socket.connect();
    // Return user
    return user;
  }

  /**
   * Associate software token...
   */
  public async associateSoftwareToken(): Promise<string> {
    // Logs
    this.logger.log('Try to retrieve software token ...');
    // Associate software token ...
    const code = await Auth.setupTOTP(this.user);
    // Logs
    this.logger.success('Software token retrieved');
    // Return code
    return code;
  }

  /**
   * Verify software token...
   */
  public async verifySoftwareToken(totpCode: string): Promise<void> {
    // Logs
    this.logger.log('Try to verify software token ...');
    // Verify software token...
    await Auth.verifyTotpToken(this.user, totpCode);
    // Set preferred MFA method
    await Auth.setPreferredMFA(this.user, 'TOTP');
    // Refresh user
    await this.getUser();
    // Logs
    this.logger.success('Software token verified');
  }

  /**
   * Retrieve 2FA preferences...
   */
  public async getPreferred2FA(): Promise<string> {
    // Logs
    this.logger.log('Try to retrieve 2FA preferences...');
    // Verify software token...
    const preferences = await Auth.getPreferredMFA(this.user, {
      bypassCache: true,
    });
    // Logs
    this.logger.success('2FA preferences retreived');
    // Return preferences
    return preferences;
  }

  /**
   * Verify software token...
   */
  public async delete2FA(): Promise<void> {
    // Logs
    this.logger.log('Try to remove 2FA ...');
    // Verify software token...
    await Auth.setPreferredMFA(this.user, 'NOMFA');
    // Refresh user
    await this.getUser();
    // Logs
    this.logger.success('2FA removed');
  }

  /**
   * Sign out
   */
  public signOut(): Promise<void> {
    // Logs
    this.logger.info('Sign out ...');
    // Reset stored user
    this.$commit('SET_USER', null);
    // Close repository
    this.Vue.prototype.$pdc.repository.socket.disconnect();
    // Clear local storage
    this.Vue.prototype.$pdc.storage.removeUniversal('readyToConfigure');
    // Clear analysis
    this.Vue.prototype.$pdc.analysis.clean(true);
    // Return promise
    return Auth.signOut();
  }
}
